import { useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Loader2, RefreshCw } from 'lucide-react'
import type { Transaction } from './types'
import { useFXRate, useRefreshFXRate } from './hooks/useReconApi'

interface CommitDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  transactions: Transaction[]
  clientId: string
  loading?: boolean
  onConfirm: (overrides: {
    doc_nom: string
    reg_date: string
    fx_rate?: string
    include_fx_adjustment?: boolean
  }) => void
  triggerLabel: string
}

function computeDefaults(transactions: Transaction[]): { docNom: string; date: string } {
  const today = new Date()
  let latest: Date | null = null
  for (const t of transactions) {
    for (const cp of t.match?.counterparts ?? []) {
      if (!cp.doc_date) continue
      const d = new Date(cp.doc_date)
      if (!isNaN(d.getTime()) && (!latest || d > latest)) {
        latest = d
      }
    }
  }
  const refDate = latest ?? today
  const month = refDate.getMonth() + 1
  const docNom = String(month).padStart(10, '0')

  const sameMonth =
    refDate.getFullYear() === today.getFullYear() &&
    refDate.getMonth() === today.getMonth()
  const dateValue = sameMonth
    ? today
    : new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0)

  const yyyy = dateValue.getFullYear()
  const mm = String(dateValue.getMonth() + 1).padStart(2, '0')
  const dd = String(dateValue.getDate()).padStart(2, '0')
  return { docNom, date: `${yyyy}-${mm}-${dd}` }
}

// pickForeignCurrency picks the single non-base currency present across the
// txns being committed. Returns null when all txns are in base or when more
// than one foreign currency is present (we only support a single FX rate
// override per dialog — mixed-currency bulk commit is rare and the user can
// commit those one by one).
function pickForeignCurrency(transactions: Transaction[], baseCode: string): string | null {
  const set = new Set<string>()
  for (const t of transactions) {
    if (t.currency && t.currency.toUpperCase() !== baseCode) {
      set.add(t.currency.toUpperCase())
    }
  }
  if (set.size !== 1) return null
  return [...set][0]
}

export function CommitDialog({
  open,
  onOpenChange,
  transactions,
  clientId,
  loading,
  onConfirm,
  triggerLabel,
}: CommitDialogProps) {
  const defaults = useMemo(() => computeDefaults(transactions), [transactions])
  const [docNom, setDocNom] = useState(defaults.docNom)
  const [date, setDate] = useState(defaults.date)
  const [fxRate, setFxRate] = useState('')
  const [includeFxAdjust, setIncludeFxAdjust] = useState(true)

  // Probe the firm's base currency by querying the FX-rate endpoint with a
  // sentinel: any currency request returns base_code in the response. We
  // pass the txn currency when we have one, else fall back to a known code.
  const probeCurrency =
    transactions[0]?.currency || 'EUR'
  const { data: probe } = useFXRate(clientId, probeCurrency, date)
  const baseCode = probe?.base_code || ''

  const foreignCurrency = baseCode
    ? pickForeignCurrency(transactions, baseCode)
    : null

  const { data: fx, isFetching: fxLoading } = useFXRate(
    clientId,
    foreignCurrency,
    foreignCurrency ? date : null,
  )
  const refreshFx = useRefreshFXRate()

  useEffect(() => {
    if (open) {
      setDocNom(defaults.docNom)
      setDate(defaults.date)
      setFxRate('')
      setIncludeFxAdjust(true)
    }
  }, [open, defaults.docNom, defaults.date])

  // Prefill the rate field whenever the lookup returns a fresh value.
  useEffect(() => {
    if (fx?.available && fx.rate) {
      setFxRate(fx.rate)
    } else {
      setFxRate('')
    }
  }, [fx?.available, fx?.rate])

  const handleRefresh = () => {
    if (!foreignCurrency) return
    refreshFx.mutate({ date, currency: foreignCurrency })
  }

  const count = transactions.length
  const canSubmit =
    !!docNom.trim() &&
    !!date &&
    !loading &&
    (!foreignCurrency || !!fxRate.trim())

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Commit to Mirage</DialogTitle>
          <DialogDescription>
            {count === 1
              ? 'This will write the journal entry to Mirage ERP.'
              : `This will write ${count} journal entries to Mirage ERP.`}{' '}
            All lines will use the document number and date below.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-1.5">
            <Label htmlFor="commit-doc-nom">Document number</Label>
            <Input
              id="commit-doc-nom"
              value={docNom}
              onChange={(e) => setDocNom(e.target.value)}
              placeholder="0000000005"
            />
            <p className="text-xs text-muted-foreground">
              Convention: last 1-2 digits encode the month.
            </p>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="commit-date">Date</Label>
            <Input
              id="commit-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          {foreignCurrency && (
            <div className="grid gap-1.5 rounded-md border border-amber-200 bg-amber-50/40 p-3">
              <Label htmlFor="commit-fx-rate" className="flex items-center justify-between">
                <span>
                  FX rate ({baseCode} per 1 {foreignCurrency})
                </span>
                {fx?.source && fx.available && (
                  <span className="text-[10px] font-normal text-neutral-500">
                    source: {fx.source}
                  </span>
                )}
              </Label>
              <div className="flex gap-2">
                <Input
                  id="commit-fx-rate"
                  value={fxRate}
                  onChange={(e) => setFxRate(e.target.value)}
                  placeholder={fxLoading ? 'Loading...' : 'e.g. 1.7042'}
                  disabled={fxLoading}
                  className="font-mono"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={refreshFx.isLoading}
                  title="Fetch current rate from BNB / ECB"
                >
                  {refreshFx.isLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                </Button>
              </div>
              {!fx?.available && !fxLoading && (
                <p className="text-xs text-amber-700">
                  No rate found for {foreignCurrency} on {date}. Click ↻ to fetch from BNB/ECB or enter manually.
                </p>
              )}
              <label className="flex items-start gap-2 pt-1 text-xs text-neutral-700">
                <Checkbox
                  checked={includeFxAdjust}
                  onCheckedChange={(v) => setIncludeFxAdjust(v === true)}
                  className="mt-0.5"
                />
                <span>
                  Bundle FX adjustment line (624 / 724) when the matched invoice was
                  recorded at a different rate.
                </span>
              </label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            onClick={() =>
              onConfirm({
                doc_nom: docNom.trim(),
                reg_date: date,
                fx_rate: foreignCurrency ? fxRate.trim() : undefined,
                include_fx_adjustment: foreignCurrency ? includeFxAdjust : undefined,
              })
            }
          >
            {loading ? 'Committing...' : triggerLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
